// Identidad anónima estilo Nostr: al primer ingreso se genera un par de claves
// secp256k1; la privada queda SOLO en el dispositivo (localStorage) y la pública
// (pubkey) sirve de ID anónimo para la analítica. Mismo enfoque que el juego de GO
// del taller de IA (nostr-tools), pero sin publicar a relays.
import { generateSecretKey, getPublicKey, finalizeEvent } from 'nostr-tools/pure';

const KEY = 'm26:identity';

export interface Identity {
  sk: string; // clave privada (hex) — nunca sale del dispositivo
  pub: string; // clave pública (hex) — ID anónimo
  created: number;
}

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function fromHex(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return out;
}

// ¿Es la primera vez? (consultar ANTES de getIdentity, que la crea si falta)
export function isFirstVisit(): boolean {
  try {
    return localStorage.getItem(KEY) === null;
  } catch {
    return false;
  }
}

export function getIdentity(): Identity {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as Identity;
  } catch {
    /* localStorage no disponible */
  }
  const sk = generateSecretKey();
  const id: Identity = { sk: toHex(sk), pub: getPublicKey(sk), created: Date.now() };
  try {
    localStorage.setItem(KEY, JSON.stringify(id));
  } catch {
    /* ignorar */
  }
  return id;
}

// Firma Schnorr (BIP340) de un objeto, con la clave del dispositivo. Sirve para que el
// backend pueda, a futuro, verificar la autoría de un pronóstico (hoy guarda la firma sin
// verificarla — ver endpoints/timba.php). Si algo falla, devuelve '' (no bloquea el envío).
export function signData(obj: unknown): string {
  try {
    const id = getIdentity();
    const ev = finalizeEvent(
      { kind: 27235, created_at: Math.floor(Date.now() / 1000), tags: [], content: JSON.stringify(obj) },
      fromHex(id.sk),
    );
    return ev.sig;
  } catch {
    return '';
  }
}

// Credencial de escritura (token) para la Timba: hash determinístico de la clave privada.
// Cualquier dispositivo con la misma clave la recalcula → no rompe "importar la clave".
// El backend hace TOFU: la fija la 1ª vez que ve el pub y después la exige, así nadie puede
// escribir con la identidad de otro (los pub son públicos en la tabla de posiciones).
// Devuelve '' si el contexto no es seguro (ej. HTTP en LAN) → el backend cae al modo sin protección.
export async function getWriteToken(): Promise<string> {
  try {
    const id = getIdentity();
    const data = new TextEncoder().encode('vamovamo:timba:write:' + id.sk);
    const buf = await crypto.subtle.digest('SHA-256', data);
    return toHex(new Uint8Array(buf));
  } catch {
    return '';
  }
}

// Respaldo: la clave secreta (hex) para guardar en lugar seguro y poder restaurar la
// identidad (mismos pronósticos y torneos) en otro dispositivo.
export function exportSecretKey(): string {
  return getIdentity().sk;
}

// Restaura la identidad desde una clave secreta hex (64). Lanza si es inválida.
export function importSecretKey(skHex: string): Identity {
  const clean = skHex.trim().toLowerCase().replace(/[^a-f0-9]/g, '');
  if (clean.length !== 64) {
    throw new Error('Clave inválida: deben ser 64 caracteres hexadecimales.');
  }
  const id: Identity = { sk: clean, pub: getPublicKey(fromHex(clean)), created: Date.now() };
  localStorage.setItem(KEY, JSON.stringify(id));
  return id;
}
